import React from "react";
import styled from "styled-components/macro";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { TextField, FormControl, InputLabel, Input, InputAdornment, IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useIntl } from "react-intl";
import translate from "../providers/i18n/translate";
import { ThemeContext } from "../providers/theme";
import { useAuth } from "../providers/authentication";
import { API_URL_UM, TELEGRAM_BOT_USERNAME } from "../config";

const Root = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 80vw;
    height: auto;
    margin: 2rem auto;
    @media (min-width: 1024px) {
        width: 350px;
        min-height: 300px;
    }
    input:-webkit-autofill {
        transition: background-color 5000s ease-in-out 0s, color 5000s ease-in-out 0s;
        transition-delay: background-color 5000s, color 5000s;
    }
`

const Title = styled.h2`
    width: 80%;
    border-bottom: 1px solid rgba(0,0,0,0.8);
    text-transform: uppercase;
    text-align: center;
    line-height: 3rem;
    margin: 0 0 30px 0;
`

const StyledButton = styled.button`
    border-radius: 47px;
    background: ${(props) => props.bg ? props.bg : '#D2A757'};
    padding: 10px 30px;
    margin: 30px 0;
    box-shadow: none;
    user-select: none;
    outline: none;
    border: none;
    cursor: pointer;
    color: white;
    text-decoration: none;
    display: inline-block;
`

const MessageText = styled.div`
    margin-top: 16px;
    text-align: center;
    font-size: 0.875rem;
`

const ErrorText = styled.p`
    color: #b00020;
    font-size: 0.875rem;
    margin: 0.5rem 0 0 0;
    text-align: center;
`

const LoginForm = styled.form`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
`

const inFlightBinds = new Set();

export default function TelegramBindPage() {
    const intl = useIntl();
    const theme = React.useContext(ThemeContext);
    const [searchParams] = useSearchParams();
    const bindToken = searchParams.get("token") || "";
    const { token, onLogin } = useAuth();

    const [error, setError] = React.useState("");
    const [done, setDone] = React.useState(false);
    const [binding, setBinding] = React.useState(false);
    const [formData, setFormData] = React.useState({ username: "", password: "" });
    const [showPassword, setShowPassword] = React.useState(false);

    const deepLink = TELEGRAM_BOT_USERNAME
        ? `https://t.me/${TELEGRAM_BOT_USERNAME}?start=bound`
        : null;

    const completeBind = (jwt) => {
        if (!bindToken.trim() || !jwt || inFlightBinds.has(bindToken)) {
            return;
        }
        inFlightBinds.add(bindToken);
        setBinding(true);
        setError("");
        axios
            .post(
                `${API_URL_UM}/api/users/telegram/bind`,
                { token: bindToken.trim() },
                {
                    withCredentials: true,
                    headers: { Authorization: `Bearer ${jwt}` },
                }
            )
            .then(() => {
                setDone(true);
                if (deepLink) {
                    window.location.assign(deepLink);
                }
            })
            .catch((err) => {
                inFlightBinds.delete(bindToken);
                setBinding(false);
                setError(err.response?.data?.msg || intl.formatMessage({ id: "telegramBindGenericError" }));
            });
    };

    React.useEffect(() => {
        if (!bindToken.trim()) {
            return;
        }
        const jwt = token || JSON.parse(localStorage.getItem("token"));
        if (jwt) {
            completeBind(jwt);
        }
    }, [bindToken, token]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const submitLogin = (e) => {
        e.preventDefault();
        setError("");
        onLogin({ userName: formData.username, password: formData.password }, () => {
            completeBind(JSON.parse(localStorage.getItem("token")));
        });
    };

    if (!bindToken.trim()) {
        return (
            <Root>
                <Title>{translate("telegramBindTitle")}</Title>
                <MessageText>{translate("telegramBindMissingToken")}</MessageText>
            </Root>
        );
    }

    if (done) {
        return (
            <Root>
                <Title>{translate("telegramBindTitle")}</Title>
                <MessageText>{translate("telegramBindSuccess")}</MessageText>
                {deepLink ? (
                    <StyledButton as="a" href={deepLink} bg={theme.palette.accent}>
                        {translate("telegramBindOpenTelegram")}
                    </StyledButton>
                ) : (
                    <ErrorText>{translate("telegramBindMissingBotUsername")}</ErrorText>
                )}
            </Root>
        );
    }

    if (error) {
        return (
            <Root>
                <Title>{translate("telegramBindTitle")}</Title>
                <ErrorText>{error}</ErrorText>
            </Root>
        );
    }

    if (binding || token || JSON.parse(localStorage.getItem("token"))) {
        return (
            <Root>
                <Title>{translate("telegramBindTitle")}</Title>
                <MessageText>{translate("telegramBindBinding")}</MessageText>
            </Root>
        );
    }

    return (
        <Root>
            <Title>{translate("telegramBindTitle")}</Title>
            <MessageText>{translate("telegramBindLoginHint")}</MessageText>
            <LoginForm onSubmit={submitLogin}>
                <TextField
                    label={translate("username")}
                    name="username"
                    type="text"
                    value={formData.username}
                    onChange={handleChange}
                    variant="standard"
                    margin="normal"
                    fullWidth
                />
                <FormControl variant="standard" fullWidth margin="normal">
                    <InputLabel htmlFor="telegram-bind-password">{translate("password")}</InputLabel>
                    <Input
                        id="telegram-bind-password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={handleChange}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                </FormControl>
                <StyledButton type="submit" bg={theme.palette.accent}>
                    {translate("loginbutton")}
                </StyledButton>
            </LoginForm>
        </Root>
    );
}
